import { useRef, useState } from "react";
import { Button } from "react-bootstrap";
import html2canvas from "html2canvas-pro";
import jsPDF from "jspdf";
import { FiPrinter, FiUpload, FiDownload, FiLoader } from "react-icons/fi";
import { useUpdateBooking } from "../../../../../api/fleet-management/bookings/booking.hooks";
import {
  useDeleteMediaMutation,
  useUploadMediaMutation,
} from "../../../../../api/fleet-management/media/media.hooks";
import {
  extractFleetMediaFilename,
  getFleetMediaUrl,
  getFleetUploadRelativeUrl,
} from "../../../../../api/fleet-management/api";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("en-PK", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("en-PK", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }).format(date);
};

const getIdValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value?._id || value?.id || "";
};

const BookingVoucherPrintButton = ({ booking, assignment }) => {
  const voucherRef = useRef(null);
  const uploadInputRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const { mutateAsync: uploadMedia, isLoading: isUploadingVoucher } = useUploadMediaMutation();
  const { mutateAsync: deleteMedia, isLoading: isDeletingVoucher } = useDeleteMediaMutation();
  const { mutateAsync: updateBooking, isLoading: isUpdatingBooking } = useUpdateBooking();

  if (!booking || !assignment) return null;

  const driver = assignment?.driverSnapshot || assignment?.driverId || null;
  if (!driver) return null;

  const vehicleId = getIdValue(assignment?.vehicleId);
  const vehicles = Array.isArray(booking?.vehicles) ? booking.vehicles : [];
  const vehicle = vehicles.find((item) => getIdValue(item?.vehicleId) === vehicleId) || null;

  const customerName = booking?.customerSnapshot?.name || booking?.fullName || "-";
  const companyName = booking?.customerSnapshot?.companyName || booking?.companyName || "-";
  const customerPhone = booking?.customerSnapshot?.phoneNumber || booking?.phoneNumber || "-";
  const customerEmail = booking?.customerSnapshot?.email || booking?.email || "-";

  const vehicleMakeModel =
    [vehicle?.snapshot?.make, vehicle?.snapshot?.model].filter(Boolean).join(" ") ||
    [vehicle?.vehicleId?.make, vehicle?.vehicleId?.model].filter(Boolean).join(" ") ||
    "-";
  const vehicleRegistration = vehicle?.vehicleId?.registrationNumber || vehicle?.snapshot?.registrationNumber || "-";
  const vehiclePrice =
    vehicle?.price !== undefined && vehicle?.price !== null
      ? String(vehicle.price)
      : vehicle?.vehicleId?.price !== undefined && vehicle?.vehicleId?.price !== null
      ? String(vehicle.vehicleId.price)
      : "-";

  const voucherCode = assignment?.voucher || `VCH-${String(booking?._id || "").slice(-6).toUpperCase()}`;
  const displayBookingId = booking?.bookingId || booking?._id || "-";
  const currentVoucherPath = assignment?.voucher || "";

  const isSameAssignment = (source, target) => {
    const sourceVehicleId = getIdValue(source?.vehicleId);
    const sourceDriverId = getIdValue(source?.driverId);
    const targetVehicleId = getIdValue(target?.vehicleId);
    const targetDriverId = getIdValue(target?.driverId);

    return sourceVehicleId === targetVehicleId && sourceDriverId === targetDriverId;
  };

  const sectionTitleStyle = {
    marginBottom: "10px",
    fontSize: "16px",
    fontWeight: 700,
    letterSpacing: "0.2px",
  };

  const sectionBoxStyle = {
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    padding: "12px 14px",
    marginBottom: "16px",
  };

  const sectionDividerStyle = {
    borderTop: "1px dashed #9ca3af",
    margin: "6px 0 14px",
  };

  const handleDownloadPdf = async () => {
    if (!voucherRef.current || isGenerating) return;

    setIsGenerating(true);

    try {
      const canvas = await html2canvas(voucherRef.current, {
        scale: 3,
        useCORS: true,
        // backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.72);
      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
        compress: false,
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const imgWidth = pageWidth - margin * 2;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = margin;

      pdf.addImage(imgData, "JPEG", margin, position, imgWidth, imgHeight, undefined, "FAST");
      heightLeft -= pageHeight - margin * 2;

      while (heightLeft > 0) {
        pdf.addPage();
        position = margin - (imgHeight - heightLeft);
        pdf.addImage(imgData, "JPEG", margin, position, imgWidth, imgHeight, undefined, "FAST");
        heightLeft -= pageHeight - margin * 2;
      }

      const safeDriverName = (driver?.name || "driver").toLowerCase().replace(/\s+/g, "-");
      const safeBookingId = String(displayBookingId).toLowerCase().replace(/\s+/g, "-");
      const fileName = `booking-voucher-${safeBookingId}-${safeDriverName}.pdf`;
      pdf.save(fileName);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUploadVoucherClick = () => {
    if (isUploadingVoucher || isUpdatingBooking || isDeletingVoucher) return;
    uploadInputRef.current?.click();
  };

  const handleUploadVoucherFile = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file || !booking?._id) return;

    try {
      const uploadResult = await uploadMedia(file);
      const relativePath = getFleetUploadRelativeUrl(uploadResult, 0);

      if (!relativePath) {
        throw new Error("Uploaded voucher path not found.");
      }

      const existingAssignments = Array.isArray(booking?.driverAssignments)
        ? booking.driverAssignments
        : [];

      const nextDriverAssignments = existingAssignments.map((item) => {
        const mappedAssignment = {
          vehicleId: getIdValue(item?.vehicleId) || item?.vehicleId,
          driverId: getIdValue(item?.driverId) || item?.driverId,
          voucher: item?.voucher || "",
        };

        if (item?.driverSnapshot) {
          mappedAssignment.driverSnapshot = item.driverSnapshot;
        }

        if (isSameAssignment(item, assignment)) {
          mappedAssignment.voucher = relativePath;
        }

        return mappedAssignment;
      });

      await updateBooking({
        bookingId: booking._id,
        bookingData: {
          driverAssignments: nextDriverAssignments,
        },
      });

      // If voucher is being replaced, remove old file from media store.
      if (currentVoucherPath && currentVoucherPath !== relativePath) {
        const previousFilename = extractFleetMediaFilename(currentVoucherPath);
        if (previousFilename) {
          try {
            await deleteMedia({ filename: previousFilename });
          } catch (deleteError) {
            console.error("Failed to delete previous voucher:", deleteError);
          }
        }
      }
    } catch (uploadError) {
      console.error("Failed to upload/update voucher:", uploadError);
    } finally {
      event.target.value = "";
    }
  };

  const handleDownloadVoucher = async () => {
    if (!currentVoucherPath || isDownloading) return;

    setIsDownloading(true);

    try {
      const voucherUrl = getFleetMediaUrl(currentVoucherPath);
      const response = await fetch(voucherUrl);

      if (!response.ok) {
        throw new Error("Failed to download voucher file.");
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      const safeDriverName = (driver?.name || "driver").toLowerCase().replace(/\s+/g, "-");
      const safeBookingId = String(displayBookingId).toLowerCase().replace(/\s+/g, "-");
      const extension = currentVoucherPath.split(".").pop() || "pdf";
      const fileName = `voucher-${safeBookingId}-${safeDriverName}.${extension}`;

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (downloadError) {
      console.error("Failed to download voucher:", downloadError);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
          display: inline-block;
        }
      `}</style>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <Button 
          size="sm" 
          variant="outline-primary" 
          onClick={handleDownloadPdf} 
          disabled={isGenerating}
          title="Print Voucher"
        >
          {isGenerating ? <FiLoader className="spin" /> : <FiPrinter />}
        </Button>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={handleUploadVoucherClick}
          disabled={isUploadingVoucher || isUpdatingBooking || isDeletingVoucher}
          title="Upload Voucher"
        >
          {isUploadingVoucher || isUpdatingBooking || isDeletingVoucher ? <FiLoader className="spin" /> : <FiUpload />}
        </Button>
        {currentVoucherPath && (
          <Button
            size="sm"
            variant="outline-success"
            onClick={handleDownloadVoucher}
            disabled={isDownloading}
            title="Download Voucher"
          >
            {isDownloading ? <FiLoader className="spin" /> : <FiDownload />}
          </Button>
        )}
        <input
          ref={uploadInputRef}
          type="file"
          accept=".pdf,image/*"
          style={{ display: "none" }}
          onChange={handleUploadVoucherFile}
        />
      </div>

      <div
        style={{
          position: "fixed",
          left: "-99999px",
          top: 0,
          width: "794px",
          pointerEvents: "none",
          zIndex: -1,
        }}
      >
        <div
          ref={voucherRef}
          style={{
            width: "794px",
            background: "#fff",
            color: "#111827",
            padding: "28px",
            fontFamily: "Arial, sans-serif",
            fontSize: "12px",
            lineHeight: 1.6,
            border: "1px solid #e5e7eb",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "16px" }}>
            <div>
              <h2 style={{ margin: 0, fontSize: "26px", fontWeight: 800, letterSpacing: "0.3px" }}>
                Booking Voucher
              </h2>
              <div style={{ fontSize: "12px", marginTop: "2px" }}>Fleet Management</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div><strong>Voucher #:</strong> {voucherCode}</div>
              <div><strong>Booking ID:</strong> {displayBookingId}</div>
              <div><strong>Generated:</strong> {formatDateTime(new Date())}</div>
            </div>
          </div>

          <div style={{ borderTop: "2px solid #111827", marginBottom: "16px" }} />

          <div style={sectionTitleStyle}>Customer Details</div>
          <div style={sectionBoxStyle}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
              <div><strong>Name:</strong> {customerName}</div>
              <div><strong>Company:</strong> {companyName}</div>
              <div><strong>Phone:</strong> {customerPhone}</div>
              <div><strong>Email:</strong> {customerEmail}</div>
            </div>
          </div>

          <div style={sectionDividerStyle} />

          <div style={sectionTitleStyle}>Trip Details</div>
          <div style={sectionBoxStyle}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
              <div><strong>Pickup Location:</strong> {booking?.pickupLocation || "-"}</div>
              <div><strong>Dropoff Location:</strong> {booking?.dropoffLocation || "-"}</div>
              <div><strong>Pickup Date & Time:</strong> {formatDateTime(booking?.pickupDateTime)}</div>
              <div><strong>Dropoff Date & Time:</strong> {formatDateTime(booking?.dropoffDateTime)}</div>
            </div>
          </div>

          <div style={sectionDividerStyle} />

          <div style={sectionTitleStyle}>Vehicle Details</div>
          <div style={sectionBoxStyle}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
              <div><strong>Vehicle:</strong> {vehicleMakeModel}</div>
              <div><strong>Registration #:</strong> {vehicleRegistration}</div>
              <div><strong>Price:</strong> {vehiclePrice === "-" ? "-" : `PKR ${vehiclePrice}`}</div>
              <div><strong>Status:</strong> {(booking?.status || "-").replace("_", " ")}</div>
            </div>
          </div>

          <div style={sectionDividerStyle} />

          <div style={sectionTitleStyle}>Driver Details</div>
          <div style={sectionBoxStyle}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
              <div><strong>Name:</strong> {driver?.name || "-"}</div>
              <div><strong>CNIC:</strong> {driver?.cnic || "-"}</div>
              <div><strong>Contact:</strong> {driver?.contactNumber || "-"}</div>
              <div><strong>License #:</strong> {driver?.licenseNumber || driver?.license?.number || "-"}</div>
              <div><strong>License Expiry:</strong> {formatDate(driver?.licenseExpiryDate || driver?.license?.expiryDate)}</div>
            </div>
          </div>

          <div style={{ borderTop: "2px dashed #6b7280", marginTop: "10px", paddingTop: "24px" }}>
            <div style={{ fontWeight: 700, marginBottom: "24px" }}>Signatures</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px" }}>
              <div>
                <div style={{ borderBottom: "1px solid #111827", height: "54px" }} />
                <div style={{ marginTop: "8px", textAlign: "center", fontWeight: 600 }}>Driver Signature</div>
              </div>
              <div>
                <div style={{ borderBottom: "1px solid #111827", height: "54px" }} />
                <div style={{ marginTop: "8px", textAlign: "center", fontWeight: 600 }}>Customer Signature</div>
              </div>
              <div>
                <div style={{ borderBottom: "1px solid #111827", height: "54px" }} />
                <div style={{ marginTop: "8px", textAlign: "center", fontWeight: 600 }}>
                  Office Representative / Manager
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BookingVoucherPrintButton;
